
import {
  ConfirmedStatusType,
  ContactRequestStatus,
  ContactType,
  CustomOfferRequestStatus,
  DrawingRequestStatus,
  FileExtension,
  RealEstateExternalActionStatus,
  SelectionType,
  UploadFileType
} from '@/__generated__/globalTypes'
import DetailsList from '@/components/detailsList/DetailsList.vue'
import ExpandableList from '@/components/expandableList/ExpandableList.vue'
import QUERIES from '@/queries/queries'
import { RouteNames } from '@/router/routeNames'
import { toLocaleDateString, toLocaleDateTimeString } from '@/utils/dateUtils'
import { Component, Vue } from 'vue-property-decorator'
import { Location } from 'vue-router'
import {
  ExternalActionFragment,
  FileFragment,
  MessageFragment,
  RealEstateDetailsDocument,
  RealEstateDetailsQuery,
  RealEstateDetailsQueryVariables,
  RealEstateFragment
} from './gql/__generated__/realEstateDetails.query'
import { RealEstateDetails3DDrawingRequest } from './types'

@Component({
  components: {
    DetailsList,
    ExpandableList
  }
})
export default class RealEstateDetailsView extends Vue {
  realEstateData: RealEstateFragment | null = null
  fileDownloadLoading: Record<string, boolean> = {}

  RouteNames = RouteNames
  RealEstateExternalActionStatus = RealEstateExternalActionStatus
  FileExtension = FileExtension
  DrawingRequestStatus = DrawingRequestStatus
  CustomOfferRequestStatus = CustomOfferRequestStatus
  ContactRequestStatus = ContactRequestStatus
  ContactType = ContactType

  expandAllNotifications = false
  expandAllCustomOfferAppointments = false
  expandAll3DDrawingRequests = false
  expandAllFiles = false
  expandAllContactRequests = false

  toLocaleDateTimeString = toLocaleDateTimeString

  DAY_IN_MILLISECONDS = 86400000

  created() {
    this.$apollo.addSmartQuery<RealEstateDetailsQuery, RealEstateDetailsQueryVariables>(
      QUERIES.RealEstateDetailsQuery,
      {
        query: RealEstateDetailsDocument,
        variables: () => ({ id: this.$route.params?.realEstateId }),
        fetchPolicy: 'network-only',
        update: (data) => data,
        result: ({ data }) => {
          this.realEstateData = data?.realEstate ?? null
        },
        error: (error) => {
          this.$store.dispatch('showErrorDialog', {
            Code: 'E4212',
            Message: error
          })
        }
      }
    )
  }

  get language() {
    return this.$i18n.locale
  }

  get statusTranslated() {
    const status = this.realEstateData?.activeStatus?.status
    if (!status) {
      return ''
    }

    return this.$t(`global.realEstateStatuses.${status}`)
  }

  get closingDate() {
    const date = toLocaleDateTimeString(this.realEstateData?.closeDateUtc, this.language)
    if (date) return date.charAt(0).toUpperCase() + date.slice(1)
    return ''
  }

  get contact() {
    if (!this.realEstateData?.contact) return null
    return {
      email: this.realEstateData?.contact.email.address,
      legalName: this.realEstateData?.contact.legalName,
      phone: this.realEstateData?.contact.phone?.number,
      invitedDate: toLocaleDateString(this.realEstateData?.contact.invitationSent, this.language),
      lastLogin: toLocaleDateTimeString(this.realEstateData?.contact.lastLogin, this.language)
    }
  }

  get realEstateDetails() {
    if (!this.realEstateData) return null
    return {
      offerNumber: this.realEstateData?.revisionNumber || this.realEstateData?.number,
      layout: this.realEstateData?.layout?.name,
      rooms: this.rooms?.flatMap((room) => room?.name).join(', '),
      notificationsEnabled: this.realEstateData?.contact?.isNotificationsEnabled
        ? this.$t('global.yes')
        : this.$t('global.no')
    }
  }

  get notifications() {
    return (
      this.realEstateData?.contact?.notifications.map((notification) => ({
        id: notification.id,
        type: this.$t(`realEstateDetails.notifications.${notification.notificationType}`),
        sentDate: toLocaleDateTimeString(notification.sentDate, this.language, false)
      })) ?? []
    )
  }

  get rooms() {
    return this.realEstateData?.layout?.layoutRooms?.map((x) => x.room)
  }

  get offerRequestedDate() {
    return toLocaleDateTimeString(this.realEstateData?.realEstateOrder?.createdDate, this.language)
  }

  get dossierSentDate() {
    return toLocaleDateTimeString(this.realEstateData?.dossierSentDateUtc, this.language)
  }

  get orderRooms() {
    return this.realEstateData?.realEstateOrder?.rooms ?? []
  }

  get customOfferStatus() {
    return this.realEstateData?.customOfferRequest?.requestStatus?.status
  }

  get customOfferStatusDisplayText() {
    return this.customOfferStatus ? this.$t(`global.customOfferStatuses.${this.customOfferStatus}`) : ''
  }

  get customOfferStatusColor() {
    switch (this.customOfferStatus) {
      case CustomOfferRequestStatus.CreateOffer:
      case CustomOfferRequestStatus.RequestForChange:
        return 'yellow'
      case CustomOfferRequestStatus.Appointment:
      case CustomOfferRequestStatus.SharedWithCustomer:
      case CustomOfferRequestStatus.Completed:
        return 'green'
      case CustomOfferRequestStatus.Cancelled:
        return 'red'
      case CustomOfferRequestStatus.New:
        return 'grey'
      default:
        return 'grey'
    }
  }

  get customOfferAppointments() {
    return (
      this.realEstateData?.customOfferRequest?.appointmentDates?.map((a) => ({
        id: a?.id,
        type: a ? this.$t(`global.customOfferAppointmentTypes.${a.type}`) : '',
        date: toLocaleDateTimeString(a?.appointmentDate, this.language, false)
      })) ?? []
    )
  }

  get realEstateFiles() {
    // The ExpandableList component requires an id for each item to use as a key, but RealEstateFileDto doesn't have an id property. Instead, use the file path as the id.
    return (
      this.realEstateData?.files.map((f) => ({
        id: f.path,
        ...f
      })) ?? []
    )
  }

  get contactRequests() {
    return (
      this.realEstateData?.requestForContacts?.map((cr) => ({
        id: cr?.id,
        sentDate: toLocaleDateTimeString(cr?.requested, this.language, false),
        contactType: cr?.contactType,
        status: cr?.requestStatus?.status ?? ContactRequestStatus.New,
        email: this.realEstateData?.contact?.email.address,
        phone: cr?.phone || this.realEstateData?.contact?.phone?.number,
        assignee: cr?.assignedAssignee?.displayName
      })) ?? []
    )
  }

  getContactRequestStatusColor(status: ContactRequestStatus) {
    switch (status) {
      case ContactRequestStatus.New:
        return 'green'
      case ContactRequestStatus.Pending:
        return 'yellow'
      case ContactRequestStatus.Closed:
      default:
        return 'grey'
    }
  }

  get externalQuestions(): ExternalActionFragment[] {
    return (
      this.realEstateData?.externalActions?.map((externalAction) => ({
        ...externalAction,
        notes: externalAction.notes?.sort((a, b) => {
          if (a?.dateCreated < b?.dateCreated) return -1
          if (a?.dateCreated > b?.dateCreated) return 1
          return 0
        })
      })) ?? []
    )
  }

  get notes() {
    return (this.realEstateData?.notes as MessageFragment[]) ?? null
  }

  get isGlobalCustomOffer(): boolean {
    return (
      !!this.offerRequestedDate &&
      (this.orderRooms.length === 0 || this.orderRooms.every((x) => x.selectionType === SelectionType.GlobalCustom))
    )
  }

  get requestsFor3DDrawings(): RealEstateDetails3DDrawingRequest[] {
    return (
      this.realEstateData?.requestFor3DDrawings.map((drawing) => ({
        id: drawing.id,
        requestedDate: drawing.requested,
        status: drawing.request3DDrawingStatus ?? DrawingRequestStatus.Requested,
        drawer: drawing.assignedDrawer?.displayName ?? '-'
      })) ?? []
    )
  }

  isRequestCancellable(request: RealEstateDetails3DDrawingRequest) {
    const requestDate = new Date(request.requestedDate)
    const currentDate = new Date()
    const diff = currentDate.getTime() - requestDate.getTime()
    return diff <= this.DAY_IN_MILLISECONDS
  }

  get3DDrawingRequestStatusLabel(request: RealEstateDetails3DDrawingRequest) {
    const statusKey =
      request.status === DrawingRequestStatus.Requested && this.isRequestCancellable(request)
        ? 'REQUESTED_AND_CANCELABLE'
        : request.status
    return this.$t(`drawings.status.${statusKey}`)
  }

  get3DDrawingRequestStatusColor(request: RealEstateDetails3DDrawingRequest) {
    switch (request.status) {
      case DrawingRequestStatus.Ready:
        return 'green'
      case DrawingRequestStatus.Cancelled:
        return 'red'
      case DrawingRequestStatus.Requested:
        return this.isRequestCancellable(request) ? 'orange darken-2' : 'yellow darken-2'
      default:
        return 'grey'
    }
  }

  getQuestionNoteDateAndTarget(externalQuestion: ExternalActionFragment) {
    return `[${toLocaleDateTimeString(externalQuestion?.requestDateUtc, this.language)}] ${
      externalQuestion?.externalActionTarget
    }:`
  }

  getQuestionNoteDate(date: string) {
    return `[${toLocaleDateTimeString(date, this.language)}]`
  }

  isUpdatedQuestion(index: number, externalQuestion: ExternalActionFragment) {
    const isUpdatedQuestionOrAnswer = index > 0
    const isQuestionOpen = externalQuestion.status !== RealEstateExternalActionStatus.Closed
    const isNotLastMessageWhenClosed =
      externalQuestion.status === RealEstateExternalActionStatus.Closed && index + 1 !== externalQuestion?.notes?.length

    return isUpdatedQuestionOrAnswer && (isQuestionOpen || isNotLastMessageWhenClosed)
  }

  isFileDownloading(item: FileFragment) {
    return !!this.fileDownloadLoading[item.path]
  }

  downloadFile(item: FileFragment) {
    this.$set(this.fileDownloadLoading, item.path, true)

    this.$fileApi
      .downloadFile(
        `/realEstate/${this.realEstateData?.id}/${encodeURIComponent(encodeURIComponent(item.path))}`,
        item.name
      )
      .finally(() => {
        this.$set(this.fileDownloadLoading, item.path, false)
      })
  }

  getConfirmedStatus(statusType: ConfirmedStatusType, selectionType: SelectionType) {
    let status = this.$t(`projectRealEstates.confirmedStatus.${statusType.toLowerCase()}`).toString()

    if (selectionType === SelectionType.Custom) {
      status += ` (${this.$t(`global.selectionTypes.CUSTOM`)})`
    }

    return status
  }

  getMessageDateAndSender(msg: MessageFragment) {
    return `[${toLocaleDateTimeString(msg.dateCreated, this.language)}] ${msg.sender}:`
  }

  getFileDate(date?: string) {
    if (!date) return ''
    return toLocaleDateTimeString(date, this.language, false)
  }

  typeTranslation(uploadFileType: UploadFileType) {
    return this.$t(`global.uploadFileTypes.${uploadFileType}`)
  }

  getPageLocation(type: UploadFileType): Location | undefined {
    switch (type) {
      case UploadFileType.TechnicalDrawing:
        return {
          name: RouteNames.REAL_ESTATE_ORDER,
          params: { projectId: this.realEstateData?.project?.id, realEstateId: this.realEstateData?.id }
        }
      case UploadFileType.CustomOffer:
        return { name: RouteNames.CUSTOM_OFFER_DETAILS, params: { id: this.realEstateData?.customOfferRequest?.id } }
      case UploadFileType.Drawing3D:
      case UploadFileType.Manual:
      default:
        return undefined
    }
  }
}
